'use client'

import {
  WAGAMOUR_OPEN_RIDE_CHOOSER,
  WAGAMOUR_PICKS_REQUEST_LOCATION,
  WAGAMOUR_PICKS_SHELL_NAVIGATE,
  type RideChooserOpenDetail,
} from '@/lib/mobility/rideChooserEvents'
import { WAGAMOUR_OPEN_REQUEST_BOOKING } from '@/lib/picks/requestBookingEvents'
import type { PicksAction } from '@/lib/picks/picksActionTypes'
import {
  buildPicksFallbackForMissingCallPhone,
  extractRawCallPhone,
  getResolvedCallPhoneForTel,
  isDialableNormalizedPhone,
  normalizePhone,
} from '@/lib/picks/callActionPhone'
import { logPicksCtaTraceForRun } from '@/lib/picks/picksCtaTrace'
import { isPicksDebugEnabled } from '@/lib/picks/picksDebug'
import {
  maybeRecordPreviewRightNowInitiation,
  maybeRecordTodayResolvedOutcome,
} from '@/lib/picks/picksActionOutcomeStore'
import { trackActionExecuted, trackDecisionClicked } from '@/lib/analytics/agentic'

/** Extra Picks call diagnostics when NEXT_PUBLIC_PICKS_DEBUG=true. */
export function picksCallDiagnosticsEnabled(): boolean {
  return isPicksDebugEnabled()
}

/**
 * Canonical Picks entry from UI: missing dialable phone on `call` → directions/view_details;
 * otherwise delegates to executePicksAction (tel, maps, etc.).
 */
export function runPicksUserAction(
  action: PicksAction,
  meta?: {
    source?: string
    ctaTrace?: { component: string; label?: string | null }
  }
): void {
  if (typeof window === 'undefined') return

  if (process.env.NODE_ENV !== 'production') {
    console.debug('[runPicksUserAction] incoming', {
      source: meta?.source,
      type: action.type,
      title: action.title,
      placeId: action.placeId,
      payload: action.payload,
    })
  }

  const effective =
    action.type === 'call' && !getResolvedCallPhoneForTel(action)
      ? buildPicksFallbackForMissingCallPhone(action)
      : action

  if (meta?.ctaTrace && picksCallDiagnosticsEnabled()) {
    logPicksCtaTraceForRun(action, effective, {
      component: meta.ctaTrace.component,
      label: meta.ctaTrace.label,
      hasAnchorWrapper: false,
      hasHref: false,
    })
  }

  trackDecisionClicked({
    action_type: effective.type,
    place_id: effective.placeId,
    title: effective.title,
    source: meta?.source,
  })

  if (action.type === 'call' && !getResolvedCallPhoneForTel(action)) {
    executePicksAction(effective)
    return
  }

  executePicksAction(action)
}

function mapsSearchNear(
  lat: number,
  lng: number,
  query: string,
  zoom: number = 14,
  viewportData: boolean = false
): string {
  const z =
    Number.isFinite(zoom) && zoom > 0
      ? Math.min(21, Math.max(1, Math.round(zoom)))
      : 14
  const suffix = viewportData ? '/data=!3m1!4b1' : ''
  return `https://www.google.com/maps/search/${encodeURIComponent(query)}/@${lat},${lng},${z}z${suffix}`
}

function mapsDirectionsToLatLng(
  lat: number,
  lng: number,
  label?: string
): string {
  const dest = label
    ? encodeURIComponent(label)
    : encodeURIComponent(`${lat},${lng}`)
  return `https://www.google.com/maps/dir/?api=1&destination=${dest}`
}

/**
 * Execute a picks action in the browser. Safe no-ops when data is missing.
 */
export function executePicksAction(action: PicksAction): void {
  if (typeof window === 'undefined') return

  if (process.env.NODE_ENV !== 'production') {
    console.debug('[executePicksAction] incoming', {
      type: action.type,
      title: action.title,
      placeId: action.placeId,
      payload: action.payload,
    })
  }

  if (process.env.NEXT_PUBLIC_PICKS_DEBUG === 'true') {
    console.log('[PICKS_ACTION_EXECUTE]', {
      type: action.type,
      placeId: action.placeId,
      payload: action.payload,
    })
  }

  maybeRecordTodayResolvedOutcome(action)
  maybeRecordPreviewRightNowInitiation(action)
  if (process.env.NEXT_PUBLIC_PREVIEW_OUTCOME_DEBUG === 'true') {
    console.log('[PREVIEW DEBUG] executePicksAction after record hooks', {
      type: action.type,
      placeId: action.placeId,
      payload: action.payload,
    })
  }

  trackActionExecuted({
    action_type: action.type,
    place_id: action.placeId,
    title: action.title,
  })

  switch (action.type) {
    case 'buy_product':
    case 'view_details':
    case 'book':
    case 'open_booking_page':
    case 'open_order_page':
    case 'open_reservation_page':
    case 'order_ahead':
    case 'book_now':
    case 'reserve_now':
      if (action.url) window.open(action.url, '_blank', 'noopener,noreferrer')
      return

    case 'call': {
      const rawPhone = extractRawCallPhone(action)
      const normalizedPhone = rawPhone ? normalizePhone(rawPhone) : null
      const dialable =
        Boolean(normalizedPhone) &&
        isDialableNormalizedPhone(normalizedPhone as string)
      if (picksCallDiagnosticsEnabled()) {
        console.debug('[PICKS CALL EXECUTE]', {
          rawPhone,
          normalizedPhone,
          hasPhone: dialable,
          usedTel: dialable,
          actionType: action?.type,
          label: action?.label,
          placeName: action.place?.name ?? action.title ?? null,
        })
      }
      if (dialable && normalizedPhone) {
        window.location.href = `tel:${normalizedPhone}`
      }
      return
    }

    case 'navigate': {
      if (
        action.lat != null &&
        action.lng != null &&
        Number.isFinite(action.lat) &&
        Number.isFinite(action.lng)
      ) {
        window.open(
          mapsDirectionsToLatLng(action.lat, action.lng, action.title),
          '_blank',
          'noopener,noreferrer'
        )
        return
      }
      if (action.url) window.open(action.url, '_blank', 'noopener,noreferrer')
      return
    }

    case 'find_nearby': {
      const q =
        (action.payload?.query as string) || action.title || 'pet services'
      const zoomRaw = action.payload?.zoom
      const zoom =
        typeof zoomRaw === 'number' && Number.isFinite(zoomRaw) ? zoomRaw : 14
      const lat = action.lat
      const lng = action.lng
      if (
        lat != null &&
        lng != null &&
        Number.isFinite(lat) &&
        Number.isFinite(lng)
      ) {
        const viewportData = action.payload?.mapsViewportData === true
        window.open(
          mapsSearchNear(lat, lng, q, zoom, viewportData),
          '_blank',
          'noopener,noreferrer'
        )
        return
      }
      if (action.url) window.open(action.url, '_blank', 'noopener,noreferrer')
      return
    }

    case 'ride_request': {
      const lat = action.lat
      const lng = action.lng
      if (
        lat != null &&
        lng != null &&
        Number.isFinite(lat) &&
        Number.isFinite(lng)
      ) {
        const payload = (action.payload || {}) as Partial<RideChooserOpenDetail>
        const detail: RideChooserOpenDetail = {
          lat,
          lng,
          title: action.title,
          city: payload.city ?? null,
          region: payload.region ?? null,
          userLat: payload.userLat,
          userLng: payload.userLng,
        }
        window.dispatchEvent(
          new CustomEvent(WAGAMOUR_OPEN_RIDE_CHOOSER, { detail })
        )
      }
      return
    }

    case 'request_booking': {
      window.dispatchEvent(
        new CustomEvent(WAGAMOUR_OPEN_REQUEST_BOOKING, {
          detail: { action },
        })
      )
      return
    }

    case 'request_location': {
      if (isPicksDebugEnabled()) {
        console.log('[PICKS LOCATION][ACTION_REQUEST_LOCATION]', {
          source: 'executePicksAction',
        })
      }
      window.dispatchEvent(new CustomEvent(WAGAMOUR_PICKS_REQUEST_LOCATION))
      return
    }

    case 'today_agent_ack':
      return

    case 'shell_navigate': {
      const tab = action.payload?.tab as 'parks' | 'picks' | string | undefined
      const picksCategory = action.payload?.picksCategory as
        | import('@/lib/picks/picksActionTypes').PicksOrchestrationCategory
        | undefined
      window.dispatchEvent(
        new CustomEvent(WAGAMOUR_PICKS_SHELL_NAVIGATE, {
          detail: { tab, picksCategory },
        })
      )
      return
    }

    case 'save': {
      try {
        const key = 'wagamour_picks_saved_places'
        const id = (action.placeId ||
          action.payload?.id ||
          action.title) as string
        if (!id) return
        const raw = window.localStorage.getItem(key)
        const list: string[] = raw ? JSON.parse(raw) : []
        if (!list.includes(String(id))) {
          list.push(String(id))
          window.localStorage.setItem(key, JSON.stringify(list.slice(-50)))
        }
      } catch {
        /* stub */
      }
      return
    }

    default:
      return
  }
}
